import Intl from '@fx-ui/intl';
import Big from 'big.js';
import { Dayjs } from 'dayjs';
import {
    forEach,
    isEmpty,
    isNil,
    isNumber,
    dayjsUtils,
    parseNumber,
    dayjs,
    leftPad,
    Region
} from '@libs/shared';
import { regionUtils } from '~/utils/region';
import { SECONDS_PER_DAY, SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from '~/constants';

/**
 * 数字大写
 *
 * @param num
 * @param isCapital
 */
export const _num2CN = (num: any, isCapital: any) => {
    // eslint-disable-next-line @fx-ui/jdy-design/limit-specific-characters
    let NUM = '〇一二三四五六七八九';
    let UNIT = '个十百千万亿';
    if (isCapital) {
        NUM = '零壹贰叁肆伍陆柒捌玖';
        UNIT = '个拾佰仟万亿';
    }
    const n = Math.floor(Math.abs(num));
    const snum = toFixed(Math.abs(num));
    const sn = snum.replace(/\..*$/, '');
    const anum = snum.split('.');
    const ch = NUM;
    const ch_u = UNIT;
    const ch_o = '-.';
    const n0 = ch[0]!;
    const reg = new RegExp(ch[0] + '*$');
    const reg1 = new RegExp(ch[0] + '+', 'g');
    let str_begin = '';
    let str_end = '';
    str_begin = num < 0 ? ch_o[0]! : '';
    if (anum.length >= 2) {
        const xs = anum[1];
        if (xs) {
            str_end = ch_o[1]!;
            forEach(xs, (_v, x: number) => {
                str_end += ch[Number(xs[x])];
            });
        }
    }
    if (sn.length === 1) {
        return str_begin + ch[n] + str_end;
    } else if (sn.length <= 5) {
        let str = '';
        for (let i = 0, n = sn.length; n--; ) {
            const _num = Number(sn[i]);
            str += _num2CN(sn[i], isCapital) + (_num && n ? ch_u[n] : '');
            i++;
        }
        str = str.replace(reg1, n0);
        str = str.replace(reg, '');
        return str_begin + str + str_end;
    } else {
        let d = (sn.length / 4) >> 0;
        let y = sn.length % 4;
        let str = '';
        while (y === 0 || !ch_u[3 + d]) {
            y += 4;
            d--;
        }
        if (Number(sn.substr(0, y))) {
            str = _num2CN(sn.substr(0, y), isCapital) + ch_u[3 + d];
            const yb = sn.substr(y);
            if (yb[0] === '0') {
                str += ch[0];
            }
            str += _num2CN(yb, isCapital);
        } else {
            str = _num2CN(sn.substr(0, y), isCapital) + _num2CN(sn.substr(y), isCapital);
        }
        str = str.replace(reg1, n0);
        str = str.replace(reg, '');
        return str_begin + str + str_end;
    }
};

/**
 * 格式化文件大小的显示
 *
 * @param bytes
 * @param region
 */
export const formatFileSize = (bytes: number, region: Region) => {
    if (!isNumber(bytes)) {
        return Intl.get('unknown');
    }

    const getFormattedSize = (bytes: number) => {
        const KB = 1024;
        const MB = Math.pow(KB, 2);
        const GB = Math.pow(KB, 3);
        if (bytes >= GB) {
            return (bytes / GB).toFixed(2) + ' GB';
        }
        if (bytes >= MB) {
            return (bytes / MB).toFixed(2) + ' MB';
        }
        return (bytes / KB).toFixed(2) + ' KB';
    };

    const isNegative = bytes < 0;
    const formattedSize = getFormattedSize(Math.abs(bytes));

    const result = isNegative ? `-${formattedSize}` : formattedSize;
    return regionUtils.formatNumber({ value: result, region });
};

/**
 * 小数精确到指定位数
 *
 * @param value
 * @param precision
 */
export const toFixed = (value: number, precision?: number) => {
    const newValue = parseNumber(value);
    if (isNil(newValue)) return '';
    // 不传precision返回正常小数位数
    return new Big(newValue).toFixed(precision).toString();
};

/**
 * 是否有效数字
 *
 * @param obj
 * @returns {boolean}
 */
export const isValidNumber = (obj: any): obj is number => {
    return isNumber(obj) && !isNaN(obj) && isFinite(obj);
};

/**
 * 数字格式化
 *
 * @param value
 * @param fmt
 */
const _numberFormat = (value: any, fmt: string) => {
    let end = '';
    let text = String(value);
    if (/%$/.test(fmt)) {
        end = '%';
        value = value * 100;
        fmt = fmt.replace('%', '');
        const dot_idx = text.indexOf('.');
        if (dot_idx > -1) {
            let fixed = text.length - 3 - dot_idx;
            fixed = fixed < 0 ? 0 : fixed > 8 ? 8 : fixed;
            value = parseFloat(value.toFixed(fixed));
        }
        text = String(value);
    }
    const parts = fmt.split('.');
    const lf = parts[0];
    const rf = parts[1];
    // 处理小数部分
    if (rf !== '') {
        const precision = rf ? rf.length : 0;
        text = toFixed(parseFloat(value), precision);
        const textarr = text.split('');
        for (let i = precision; i > 0; i--) {
            if (rf?.charAt(i - 1) !== '#') {
                break;
            }
            const l: any = textarr.pop();
            if (l !== '0') {
                textarr.push(l);
                break;
            }
        }
        let last = textarr.pop();
        // 最后一位为0则舍去
        if (last === '.') {
            last = '';
        }
        text = textarr.join('') + last;
    }
    // 处理整数部分
    const texts = text.split('.');
    const lt = texts[0];
    if (/,/.test(lf!)) {
        // 千分符
        texts[0] = lt!.replace(/(\d{1,3})(?=(?:\d{3})+(?!\d))/g, '$1,');
    } else {
        const zos = lf?.match(/[0]+[0#]*$/);
        if (zos && zos.length > 0) {
            texts[0] = leftPad(lt!, zos[0].length, '0');
        }
    }
    return texts.join('.') + end;
};

/**
 * 数字转文本
 *
 * @param value
 * @param fmt
 */
export const num2Str = (value: any, fmt: string) => {
    if (isNil(value)) {
        // 原值为空，返回空字符
        return '';
    }
    let text = String(value);
    if (!text) {
        return '';
    }
    if (isValidNumber(value)) {
        text = toFixed(value);
    }
    if (isEmpty(fmt)) {
        // 格式为空，返回原字符
        return text;
    }
    let regex = /\[Num0\]/;
    if (regex.test(fmt)) {
        // [Num0],原始数据格式
        return fmt.replace(regex, text);
    }
    regex = /\[Num1\]/;
    if (regex.test(fmt)) {
        // [Num1],中文小写
        return fmt.replace(regex, _num2CN(text, false));
    }
    regex = /\[Num2\]/;
    if (regex.test(fmt)) {
        // [Num2],中文大写
        return fmt.replace(regex, _num2CN(text, true));
    }
    regex = /[#0]+,?[#0]*\.?[#0]*%?/;
    const fmts = fmt.match(regex);
    if (fmts && fmts.length > 0) {
        const rawFmt = fmts[0];
        text = _numberFormat(value, rawFmt);
        return fmt.replace(regex, text);
    }
    return fmt;
};

/**
 * 地址转文本
 *
 * @param value
 * @param text
 * @param fmt
 */
export const address2Str = (value: any, text: string, fmt: string) => {
    let result = text;
    if (fmt && value) {
        result = '';
        let showCity = true;
        if (/p/.test(fmt) && value.province) {
            result += value.province;
            if (value.province === value.city) {
                showCity = false;
            }
        }
        if (/c/.test(fmt) && value.city && showCity) {
            result += value.city;
        }
        if (/d/.test(fmt) && value.district) {
            result += value.district;
        }
        if (/a/.test(fmt) && value.detail) {
            result += value.detail;
        }
    }
    return result;
};

/**
 * 获取两Date值的秒数差.
 * 四舍五入
 *
 * @param start
 * @param end
 */
export const getDuringSeconds = (start: Dayjs, end: Dayjs) => {
    return Math.round((dayjsUtils.valueOf(start) - dayjsUtils.valueOf(end)) / 1000);
};

/**
 * 限制字数长度，超出显示省略号
 *
 * @param text
 * @param length 默认长度为10
 */
export const textLimit = (text: string, length: number | undefined) => {
    if (isEmpty(text)) {
        return '';
    }
    const maxLen = length || 10;
    if (text.length > maxLen) {
        return text.slice(0, maxLen) + '...';
    } else {
        return text;
    }
};

/**
 * 时间差格式化
 *
 * @param startDate 开始时间
 * @param endDate 结束时间
 * @returns 两个时间的diff
 */
export const format2DateInfo = (
    startDate: string | number | Date,
    endDate: string | number | Date
) => {
    const totalSeconds = dayjs(endDate).diff(dayjs(startDate), 's');

    const day = Math.trunc(totalSeconds / SECONDS_PER_DAY);
    const hour = Math.trunc((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    const minute = Math.trunc((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const second = totalSeconds % SECONDS_PER_MINUTE;

    const normalizeNegativeZero = (value: number) => (Object.is(value, -0) ? 0 : value);

    return {
        day: normalizeNegativeZero(day),
        hour: normalizeNegativeZero(hour),
        minute: normalizeNegativeZero(minute),
        second: normalizeNegativeZero(second)
    };
};
